import { history } from '@umijs/max';
import { useSession } from '@zhiyue/core';
import React from 'react';
import {
  readCurrentParkUuid,
  readCurrentTenantUuid,
} from '@/adapters/tenantStorage';
import {
  BootstrapError,
  BootstrapLoading,
  useEnterpriseBootstrap,
} from './bootstrap';
import { getEnterpriseRouteRedirect } from './routing';

const isPublicPath = (pathname: string) =>
  pathname === '/callback' || pathname.startsWith('/user');

export const EnterpriseRouteGate: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [location, setLocation] = React.useState(() => history.location);
  const { currentUser, loading: sessionLoading } = useSession();
  const {
    data,
    loading,
    refresh,
    tenantUuid: bootstrapTenantUuid,
  } = useEnterpriseBootstrap();
  const [parkContextVersion, setParkContextVersion] = React.useState(0);

  React.useEffect(
    () =>
      history.listen(({ location: nextLocation }) => {
        setLocation(nextLocation);
      }),
    [],
  );

  React.useEffect(() => {
    const handleParkContextChange = () =>
      setParkContextVersion((value) => value + 1);
    window.addEventListener('park-context-changed', handleParkContextChange);
    return () =>
      window.removeEventListener(
        'park-context-changed',
        handleParkContextChange,
      );
  }, []);

  React.useEffect(() => {
    if (!data?.state) return;
    const storedParkUuid = readCurrentParkUuid();
    const currentParkUuid = data.accessibleParks?.some(
      (park) => park.uuid === storedParkUuid,
    )
      ? storedParkUuid
      : data.currentPark?.uuid;
    const pendingApplicationUuid =
      data.pendingAdmission?.uuid ||
      data.latestPendingAdmission?.uuid ||
      data.latestAdmission?.uuid;
    const redirect = getEnterpriseRouteRedirect(
      location.pathname,
      data.state,
      data.accessibleParks ?? [],
      currentParkUuid,
      pendingApplicationUuid,
    );
    if (redirect && redirect !== location.pathname) {
      history.replace(redirect);
    }
  }, [data, location.pathname, parkContextVersion]);

  if (isPublicPath(location.pathname)) {
    return children;
  }
  const tenantUuid = currentUser?.tenantUuid || readCurrentTenantUuid();
  if (sessionLoading || !currentUser || !tenantUuid) {
    return children;
  }
  if (!data || bootstrapTenantUuid !== tenantUuid) {
    if (!loading && bootstrapTenantUuid === tenantUuid && !data) {
      return <BootstrapError onRetry={() => void refresh()} />;
    }
    return <BootstrapLoading />;
  }

  const redirect = getEnterpriseRouteRedirect(
    location.pathname,
    data.state,
    data.accessibleParks ?? [],
    data.accessibleParks?.some((park) => park.uuid === readCurrentParkUuid())
      ? readCurrentParkUuid()
      : data.currentPark?.uuid,
    data.pendingAdmission?.uuid ||
      data.latestPendingAdmission?.uuid ||
      data.latestAdmission?.uuid,
  );
  if (redirect && redirect !== location.pathname) return <BootstrapLoading />;
  return children;
};
